/**
 * investor.js — Investor AI prompt tasks (pitch deck, executive summary, outreach, FAQ, use of funds, competitive analysis, meeting prep, updates).
 * Schema and quality bar: see _exemplar.js. Template syntax: lib/ai-lab/renderTemplate.js.
 * Every money / legal template tells the AI not to invent numbers and to flag items for the founder's accountant or attorney.
 */
const STAGE_OPTIONS = ["Idea / pre-launch", "Launched — DTC only", "In local / regional retail", "In national retail", "Scaling / raising capital"];
const INSTRUMENT_OPTIONS = ["SAFE", "Priced equity round", "Convertible note", "Friends & family", "Not decided yet"];
const INVESTOR_TYPE_OPTIONS = ["Angel investor", "CPG-focused VC / seed fund", "Family office", "Strategic (CPG company or distributor)", "Friends & family", "Crowdfunding backers"];

export const INVESTOR_TASKS = [
  // ── PITCH DECK ─────────────────────────────────────────────────────────────
  {
    id: "investor-pitch-deck",
    category: "investor",
    title: "Create Pitch Deck Content",
    description: "Full slide-by-slide content for a 12–14 slide CPG seed deck — problem, product, traction, market, go-to-market, unit economics, team, the raise and use of funds — with every missing number flagged.",
    outputType: "text",
    recommendedTools: ["claude", "chatgpt", "gamma", "pitch"],
    estimatedTime: "5 min to fill · 2 min in Claude",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine, 3 flavors", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims / differentiators", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "pricePoint", label: "Retail price", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC website, Amazon, Natural grocery", brandKey: "channels" },
      { id: "stage", label: "Business stage", type: "select", options: STAGE_OPTIONS, default: "Launched — DTC only", brandKey: "stage" },
      { id: "founderStory", label: "Founder story / team background", type: "textarea", placeholder: "Why you, in 2–4 sentences; relevant experience", brandKey: "founderStory", span: "full" },
      { id: "raiseAmount", label: "Raise amount", type: "text", placeholder: "$500,000", required: true },
      { id: "instrument", label: "Instrument", type: "select", options: INSTRUMENT_OPTIONS, default: "SAFE" },
      { id: "traction", label: "Traction to date", type: "textarea", placeholder: "$180k trailing-12-month revenue; 2,100 customers; 38% repeat; 40 stores; 4.8-star reviews", required: true, span: "full" },
      { id: "useOfFunds", label: "Use of funds (rough)", type: "textarea", placeholder: "40% inventory and co-packer run; 30% retail launch and trade; 20% marketing; 10% team", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Hockey-stick charts with no basis; 'we have no competitors'", brandKey: "avoid" },
    ],
    template: `Act as a senior CPG investor and former founder who has reviewed 1,000+ consumer seed decks and coaches food and beverage founders through their first raise.

Write the complete slide-by-slide content for a 12 to 14 slide pitch deck for {{brandName}}, {{category|article}}, raising {{raiseAmount}}{{#if instrument!=Not decided yet}} (instrument: {{instrument}}){{/if}}.

PRODUCT
{{productDescription}}
{{#if pricePoint}}
Retail price: {{pricePoint}}
{{/if}}

TARGET CONSUMER
{{targetConsumer}}

POSITIONING
{{positioning}}

{{#if claims}}
DIFFERENTIATORS
{{claims|list}}
{{/if}}

{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}
{{/if}}

BUSINESS
- Stage: {{stage}}
{{#if channels}}
- Channels: {{channels|join}}
{{/if}}
- Traction: {{traction}}
- Use of funds: {{useOfFunds|default:not supplied — propose a sensible allocation for this stage and mark it [FOUNDER TO CONFIRM]}}
{{#if founderStory}}
- Team / founder: {{founderStory}}
{{/if}}

INSTRUMENT: {{instrument}}
All economic and legal terms (valuation, cap, discount, interest, maturity, pro-rata) are [TO BE SET WITH COUNSEL]; do not propose numbers or draft terms.
{{#if instrument=Friends & family}}
Keep legal language simple and note that even friends-and-family money needs proper documentation reviewed by an attorney.
{{/if}}

DECK — WRITE EVERY SLIDE IN FULL
For each slide: slide title, headline (the one sentence an investor should remember), content (bullets, table or chart description), speaker note (2 to 3 sentences), visual suggestion.
1. Title — brand, one-line positioning, raise
2. The problem / consumer tension — what {{targetConsumer}} cannot get today, in their words
3. The product — what it is, hero differentiators, price, photo
4. Why now — the category and consumer shifts (describe trend types; mark any statistic [SOURCE NEEDED] — do not invent market sizes)
5. Traction — the numbers above as a clean table or chart description; nothing not supplied
6. Business model and unit economics — price, COGS, gross margin, contribution margin by channel, CAC / LTV if DTC; show formulas with [INSERT] where numbers are missing
7. Market — TAM / SAM / SOM framework with the method to calculate each (bottom-up from shoppers x frequency x price), values as [INSERT — cite source]
8. Competition — positioning map vs. {{#if competitors}}{{competitors|join}}{{/if}}{{#unless competitors}}the leading brands{{/unless}} and where {{brandName}} wins
9. Go-to-market — channel sequence for the next 18 months and why that order
10. Moat / defensibility — brand, community, formulation, distribution, data — honest about what is early
11. Team — founder(s), relevant experience, advisors, key hires planned
12. Financial snapshot — current run-rate, 18-month targets as ranges with assumptions stated; mark [VALIDATE WITH ACCOUNTANT]
13. The raise and use of funds — amount, instrument, allocation table, milestones the money buys, runway
14. Closing / contact — the one-line reason to invest and next step

THEN PROVIDE
15. A 2-minute verbal narrative that walks the deck
16. The 5 numbers investors will ask for that are missing from the inputs

RULES
Use only supplied figures. Never invent revenue, margins, market sizes, valuations or terms — write [INSERT] or [SOURCE NEEDED]. This is not financial or legal advice; flag every term, projection and claim for the founder's accountant and securities attorney to validate before sharing with investors.

{{#if avoid}}
AVOID
{{avoid|list}}
- Hockey-stick projections with no stated assumptions
- "We have no competitors"
{{/if}}
{{#unless avoid}}
AVOID
- Hockey-stick projections with no stated assumptions
- "We have no competitors"
- Buzzwords ("disrupt", "revolutionary") and more than 5 bullets per slide
{{/unless}}`,
    tips: [
      "Paste the slide content into Gamma or Pitch in text mode, then replace every [INSERT] before it leaves your laptop.",
      "Follow up: “Cut this to a 6-slide teaser for cold outreach, keeping only traction, product, team and the ask.”",
    ],
  },

  // ── EXECUTIVE SUMMARY ──────────────────────────────────────────────────────
  {
    id: "investor-executive-summary",
    category: "investor",
    title: "Write Executive Summary",
    description: "A one-page investor executive summary — opportunity, product, traction, model, team, raise and use of funds — structured the way investors skim.",
    outputType: "text",
    recommendedTools: ["claude", "chatgpt", "gemini"],
    estimatedTime: "3 min to fill · 1 min in Claude",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims / differentiators", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC website, Amazon, Natural grocery", brandKey: "channels" },
      { id: "stage", label: "Business stage", type: "select", options: STAGE_OPTIONS, default: "Launched — DTC only", brandKey: "stage" },
      { id: "founderStory", label: "Founder / team background", type: "textarea", placeholder: "Why you, in 2–4 sentences", brandKey: "founderStory", span: "full" },
      { id: "raiseAmount", label: "Raise amount", type: "text", placeholder: "$500,000", required: true },
      { id: "instrument", label: "Instrument", type: "select", options: INSTRUMENT_OPTIONS, default: "SAFE" },
      { id: "traction", label: "Traction to date", type: "textarea", placeholder: "$180k TTM revenue; 2,100 customers; 38% repeat; 40 stores", required: true, span: "full" },
      { id: "useOfFunds", label: "Use of funds (rough)", type: "textarea", placeholder: "40% inventory; 30% retail launch; 20% marketing; 10% team", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Jargon; vague market-size claims", brandKey: "avoid" },
    ],
    template: `Act as a senior CPG investor and former operator who writes and reviews one-page investment summaries for food and beverage seed deals.

Write a one-page executive summary for {{brandName}}, {{category|article}}, raising {{raiseAmount}}{{#if instrument!=Not decided yet}} (instrument: {{instrument}}){{/if}}.

PRODUCT
{{productDescription}}

TARGET CONSUMER
{{targetConsumer}}

POSITIONING
{{positioning}}

{{#if claims}}
DIFFERENTIATORS
{{claims|join}}
{{/if}}
{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}
{{/if}}

BUSINESS
- Stage: {{stage}}
{{#if channels}}
- Channels: {{channels|join}}
{{/if}}
- Traction: {{traction}}
{{#if useOfFunds}}
- Use of funds: {{useOfFunds}}
{{/if}}
{{#unless useOfFunds}}
- Use of funds: not supplied — propose an allocation and mark it [FOUNDER TO CONFIRM]
{{/unless}}
{{#if founderStory}}
- Team: {{founderStory}}
{{/if}}
{{#unless founderStory}}
- Team: not supplied — write the team block with [INSERT] placeholders for names and backgrounds
{{/unless}}

PROVIDE
A one-page document (450 to 600 words total) with these labeled sections, in order:
1. HEADER — brand name, category, one-line positioning, raise amount and instrument, contact placeholder
2. THE OPPORTUNITY (60 to 80 words) — the consumer tension and the category shift; describe trend types, mark any statistic [SOURCE NEEDED]
3. THE PRODUCT (50 to 70 words) — what it is, the 2 to 3 differentiators that matter to an investor, price point
4. TRACTION (60 to 80 words plus a 4 to 6 row mini-table) — only the numbers supplied; table columns: Metric | Value | Period
5. BUSINESS MODEL (50 to 70 words) — channels, gross margin structure, path to contribution-margin positive; [INSERT] for missing figures
6. MARKET AND COMPETITION (50 to 70 words) — where {{brandName}} sits vs. {{#if competitors}}{{competitors|join}}{{/if}}{{#unless competitors}}incumbents{{/unless}}, and the whitespace
7. TEAM (40 to 60 words) — why these founders win
8. THE RAISE (60 to 80 words plus a short allocation table) — amount, instrument, use of funds, 3 milestones the money buys, expected runway in months [VALIDATE WITH ACCOUNTANT]
9. WHY NOW / CLOSING LINE (one sentence)

THEN PROVIDE
10. A 3-sentence version for the body of an intro email
11. The 3 weakest sections given the inputs, and what data would fix each

RULES
Use only supplied figures; never invent revenue, margins, market sizes, valuation or terms — write [INSERT] or [SOURCE NEEDED]. Not financial or legal advice: flag terms and projections for the founder's accountant and securities attorney before this is shared.

{{#if avoid}}
AVOID
{{avoid|list}}
- Jargon and buzzwords
- Unsourced market-size statements
{{/if}}
{{#unless avoid}}
AVOID
- Jargon and buzzwords
- Unsourced market-size statements
- Exceeding one page
{{/unless}}`,
    tips: [
      "Paste into Claude for the cleanest long-form prose. Then: “Tighten to 400 words without losing any number.”",
      "Export to PDF with your logo — investors forward one-pagers far more than decks.",
    ],
  },

  // ── INVESTOR OUTREACH EMAIL ────────────────────────────────────────────────
  {
    id: "investor-email",
    category: "investor",
    title: "Write Investor Outreach Email",
    description: "Cold and warm-intro investor emails under 150 words, a forwardable blurb your connector can paste, subject lines and a 3-touch follow-up.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 30 sec in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims / differentiators", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "stage", label: "Business stage", type: "select", options: STAGE_OPTIONS, default: "Launched — DTC only", brandKey: "stage" },
      { id: "founderStory", label: "Founder background (one line is fine)", type: "textarea", placeholder: "Ex-Red Bull field marketer; certified trainer", brandKey: "founderStory" },
      { id: "raiseAmount", label: "Raise amount", type: "text", placeholder: "$500,000", required: true },
      { id: "instrument", label: "Instrument", type: "select", options: INSTRUMENT_OPTIONS, default: "SAFE" },
      { id: "traction", label: "Strongest 2–3 traction points", type: "textarea", placeholder: "$180k TTM revenue; 38% repeat; approved in 60 Sprouts stores", required: true, span: "full" },
      { id: "investorType", label: "Investor type", type: "select", options: INVESTOR_TYPE_OPTIONS, default: "Angel investor" },
      { id: "investorName", label: "Investor name / firm (optional)", type: "text", placeholder: "Maria Chen, Greenhouse Ventures" },
      { id: "whyThisInvestor", label: "Why this investor (optional)", type: "text", placeholder: "Backed two functional beverage brands; spoke about clean energy at Expo West" },
      { id: "senderName", label: "Your name & title", type: "text", placeholder: "Jordan Lee, Co-founder & CEO" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "'Huge opportunity'; flattery; attachments", brandKey: "avoid" },
    ],
    template: `Act as a senior CPG investor who has received thousands of founder emails and now coaches food and beverage founders on outreach that gets a meeting.

Write investor outreach for {{brandName}} ({{category|article}}) raising {{raiseAmount}}{{#if instrument!=Not decided yet}} (instrument: {{instrument}}){{/if}}. Investor type: {{investorType}}{{#if investorName}} — specifically {{investorName}}{{/if}}.

PRODUCT
{{productDescription}}

POSITIONING
{{positioning}}

{{#if claims}}
DIFFERENTIATORS
{{claims|join}}
{{/if}}

TRACTION (lead with the strongest)
{{traction}}

{{#if founderStory}}
FOUNDER
{{founderStory}}
{{/if}}
Stage: {{stage}}

{{#if whyThisInvestor}}
WHY THIS INVESTOR
{{whyThisInvestor}}
{{/if}}
{{#unless whyThisInvestor}}
WHY THIS INVESTOR
Not supplied — leave a clearly marked [PERSONALIZE: one specific reason you chose them] line in the cold email; do not fabricate their portfolio or quotes.
{{/unless}}

{{#if investorType=Angel investor}}
Angels move on founder conviction and product love. Offer to send samples and keep it personal.
{{/if}}
{{#if investorType=CPG-focused VC / seed fund}}
Funds move on traction, velocity and a credible path to scale. Lead with numbers and name the round structure.
{{/if}}
{{#if investorType=Strategic (CPG company or distributor)}}
Strategics move on category fit and distribution synergy. Lead with the consumer gap and channel proof, not the valuation.
{{/if}}
{{#if investorType=Friends & family}}
Keep it warm and honest about risk; state clearly that documentation will be handled by an attorney.
{{/if}}

{{#if senderName}}
SIGN-OFF
{{senderName}}
{{/if}}

PROVIDE
1. COLD EMAIL — under 150 words, 3 subject-line options (under 50 characters), structure: one-line hook tied to why them, what we are and the 2 strongest traction points, the raise in one line, a low-friction ask (20-minute call or send the one-pager), signature
2. WARM-INTRO EMAIL — under 120 words, written to the investor after a mutual contact has introduced you (reply-all style), acknowledging the connector and moving them to BCC
3. FORWARDABLE BLURB — 80 to 110 words, written in third person, that your connector can paste into their own email to the investor; no hype, just facts and the ask
4. CONNECTOR ASK — a 60-word email to the mutual contact asking for the intro, with the blurb attached below and an explicit opt-out line
5. FOLLOW-UP CADENCE — 3 touches (day 6, day 14, day 28), each under 70 words with a subject line and one new piece of value (new data point placeholder, press, retail win, sample offer); touch 3 closes the loop gracefully
6. A short table: Email | Goal | Key number to lead with | Call to action

RULES
Use only supplied facts. Do not invent valuation, terms, investor details or market sizes — use [INSERT] / [PERSONALIZE]. Do not promise returns. Not legal advice: any description of terms must be marked [CONFIRM WITH COUNSEL].

{{#if avoid}}
AVOID
{{avoid|list}}
- "Huge opportunity" and flattery
- Attachments in the first email (link a one-pager instead)
{{/if}}
{{#unless avoid}}
AVOID
- "Huge opportunity" and flattery
- Attachments in the first email (link a one-pager instead)
- Asking for money before asking for a conversation
{{/unless}}`,
    tips: [
      "Send the forwardable blurb (#3) to your 5 best connectors first — warm intros convert far better than cold email.",
      "Follow up: “Rewrite the cold email for a strategic investor at a beverage distributor.”",
    ],
  },

  // ── INVESTOR FAQ ───────────────────────────────────────────────────────────
  {
    id: "investor-faq",
    category: "investor",
    title: "Create Investor FAQ",
    description: "25 tough investor questions with model answers in your voice, with every place a real number is required clearly flagged.",
    outputType: "text",
    recommendedTools: ["claude", "chatgpt", "gemini"],
    estimatedTime: "4 min to fill · 2 min in Claude",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims / differentiators", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "pricePoint", label: "Retail price", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC website, Amazon, Natural grocery", brandKey: "channels" },
      { id: "stage", label: "Business stage", type: "select", options: STAGE_OPTIONS, default: "Launched — DTC only", brandKey: "stage" },
      { id: "raiseAmount", label: "Raise amount", type: "text", placeholder: "$500,000", required: true },
      { id: "instrument", label: "Instrument", type: "select", options: INSTRUMENT_OPTIONS, default: "SAFE" },
      { id: "traction", label: "Traction to date", type: "textarea", placeholder: "$180k TTM revenue; 2,100 customers; 38% repeat; 40 stores", required: true, span: "full" },
      { id: "knownWeaknesses", label: "Known weaknesses (be honest)", type: "textarea", placeholder: "Single co-packer; CAC rising; no retail velocity data yet; solo founder", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Defensive answers; made-up numbers", brandKey: "avoid" },
    ],
    template: `Act as a senior CPG investor and former founder who runs diligence on food and beverage seed deals and coaches founders through the hardest questions.

Write an investor FAQ for {{brandName}}, {{category|article}}, raising {{raiseAmount}}{{#if instrument!=Not decided yet}} (instrument: {{instrument}}){{/if}}.

PRODUCT
{{productDescription}}
{{#if pricePoint}}
Retail price: {{pricePoint}}
{{/if}}

{{#if targetConsumer}}
TARGET CONSUMER
{{targetConsumer}}
{{/if}}

POSITIONING
{{positioning}}

{{#if claims}}
DIFFERENTIATORS
{{claims|join}}
{{/if}}
{{#if competitors}}
COMPETITORS
{{competitors|join}}
{{/if}}

BUSINESS
- Stage: {{stage}}
{{#if channels}}
- Channels: {{channels|join}}
{{/if}}
- Traction: {{traction}}
{{#if knownWeaknesses}}
- Known weaknesses: {{knownWeaknesses}}
{{/if}}
{{#unless knownWeaknesses}}
- Known weaknesses: none supplied — infer the likely ones for a {{category|lower}} brand at this stage and answer them honestly
{{/unless}}

PROVIDE
25 questions with model answers (3 to 6 sentences each, first person, in the founder's voice), grouped as follows:
A. MARKET AND CONSUMER (4) — market size and method, why this consumer, why now, what happens if the trend cools
B. PRODUCT AND DIFFERENTIATION (4) — why you win vs. {{#if competitors}}{{competitors|join}}{{/if}}{{#unless competitors}}the incumbents{{/unless}}, defensibility, formulation / IP, regulatory and claims exposure
C. TRACTION AND UNIT ECONOMICS (6) — revenue and growth, gross margin and COGS path, CAC / LTV / repeat, retail velocity, channel mix, what breaks at 10x volume
D. GO-TO-MARKET (3) — channel sequence, retail vs. DTC economics, distributor and trade-spend strategy
E. TEAM AND OPERATIONS (3) — why this team, key-person risk, supply chain and co-packer risk
F. THE RAISE (5) — why this amount, use of funds and milestones, runway, next round plan, valuation / terms, exit thinking

FORMAT RULES FOR EVERY ANSWER
- Use only supplied facts. Wherever a real number is required and not supplied, write the answer with [NUMBER NEEDED: what exactly] so the founder knows what to fill in.
- Mark each question with a difficulty (1 to 3) and a one-line "what the investor is really testing".
- For weaknesses, answer honestly: acknowledge, show what mitigates it, and what the raise fixes. Never spin.
- Valuation, terms, cap table and exit answers must not propose specific numbers or legal structures — write [DISCUSS WITH COUNSEL] and give the founder a framework for how to talk about it.

THEN PROVIDE
26. A table: Question | Difficulty | Data needed | Where to find it (books, Shopify, distributor reports, co-packer)
27. The 5 questions most likely to kill the deal given the inputs, and a prep plan for each

This is not financial, legal or investment advice. Projections, terms and claims must be validated with the founder's accountant, securities attorney and regulatory advisor.

{{#if avoid}}
AVOID
{{avoid|list}}
- Defensive or evasive answers
- Invented statistics or comparables
{{/if}}
{{#unless avoid}}
AVOID
- Defensive or evasive answers
- Invented statistics or comparables
- Jargon that hides a weak answer
{{/unless}}`,
    tips: [
      "Fill in every [NUMBER NEEDED] from your books before rehearsing — the answers only work with real numbers.",
      "Follow up: “Be a skeptical seed investor. Ask me the 5 deal-killers one at a time and push back on my answers.”",
    ],
  },

  // ── USE OF FUNDS ───────────────────────────────────────────────────────────
  {
    id: "investor-use-of-funds",
    category: "investor",
    title: "Write Use-of-Funds Plan",
    description: "A use-of-funds plan with allocation table, milestone map, runway logic and risk notes — structured as a model for your accountant to validate, not financial advice.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "google-sheets"],
    estimatedTime: "3 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "channels", label: "Sales channels (current and planned)", type: "tags", placeholder: "DTC website, Amazon, Natural grocery", brandKey: "channels" },
      { id: "pricePoint", label: "Retail price", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "stage", label: "Business stage", type: "select", options: STAGE_OPTIONS, default: "Launched — DTC only", brandKey: "stage" },
      { id: "raiseAmount", label: "Raise amount", type: "text", placeholder: "$500,000", required: true },
      { id: "instrument", label: "Instrument", type: "select", options: INSTRUMENT_OPTIONS, default: "SAFE" },
      { id: "traction", label: "Current traction & monthly burn (if known)", type: "textarea", placeholder: "$15k/month revenue; $22k/month burn; 40 stores; 2 team members", required: true, span: "full" },
      { id: "runwayMonths", label: "Target runway", type: "select", options: ["12 months", "18 months", "24 months"], default: "18 months" },
      { id: "priorities", label: "Top priorities for the money", type: "tags", placeholder: "Inventory and co-packer run, Retail launch and trade spend, Marketing, First hire, Working capital", default: "Inventory, Retail launch and trade spend, Marketing, Team, Working capital" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Spending on brand awareness before distribution; big agency retainers", brandKey: "avoid" },
    ],
    template: `Act as a senior CPG finance operator and former founder who has built use-of-funds plans for food and beverage seed rounds alongside founders' accountants.

Build a use-of-funds plan for {{brandName}}, {{category|article}}, raising {{raiseAmount}}{{#if instrument!=Not decided yet}} (instrument: {{instrument}}){{/if}}, targeting {{runwayMonths}} of runway.

PRODUCT
{{productDescription}}
{{#if pricePoint}}
Retail price: {{pricePoint}}
{{/if}}
{{#if positioning}}
Positioning: {{positioning}}
{{/if}}

BUSINESS
- Stage: {{stage}}
{{#if channels}}
- Channels: {{channels|join}}
{{/if}}
- Traction and burn: {{traction}}

PRIORITIES (in the founder's words)
{{priorities|list}}

PROVIDE
1. Allocation table — Category | % of raise | $ amount (computed from {{raiseAmount}}) | What it buys specifically | Timing (months 1–6 / 7–12 / 13+) | Type (one-time vs. recurring). Categories should cover, as relevant to {{category|lower}} at the {{stage}} stage: inventory and production runs, packaging and compliance, retail launch and trade spend (slotting, free fill, demos, TPR), marketing and customer acquisition, team and contractors, operations and systems, working-capital buffer and reserve. Show the math; percentages must sum to 100.
2. Milestone map — a table: Milestone | Month | Metric that proves it | Funds consumed to reach it | Why it matters for the next round. 4 to 6 milestones that the money is meant to buy.
3. Runway logic — a monthly burn framework: current burn (from inputs or [INSERT]), projected burn by phase, revenue contribution assumptions stated as ranges, and the resulting runway vs. the {{runwayMonths}} target. Show formulas. If the inputs do not support {{runwayMonths}}, say so and show what allocation or raise size would.
4. Inventory and cash-cycle note — how production runs, payment terms, distributor and retailer payment timing affect cash for a {{category|lower}} brand; what buffer to hold and why
5. Trade-spend note — what share of retail revenue typically goes to trade for an emerging brand (state as a typical range, mark [VERIFY]) and how that is reflected in the allocation
6. Scenario table — Base / Slower retail ramp / Faster ramp: what changes in allocation and runway for each
7. Governance — how the founder will report spend vs. plan to investors (monthly categories, variance threshold) and the 3 triggers that would cause a reallocation
8. A paste-ready version of tables 1 and 2 formatted for Google Sheets
9. The 5 assumptions the founder's accountant must validate first

RULES
This is a planning framework, not financial, tax or legal advice. Use only supplied numbers; write [INSERT] for missing ones and never invent burn, margins or costs. Instrument terms, entity and tax implications are [DISCUSS WITH ACCOUNTANT AND ATTORNEY]. State every assumption explicitly so it can be challenged.

{{#if avoid}}
AVOID
{{avoid|list}}
- Awareness spend before distribution is secured
- Unexplained "miscellaneous" buckets over 5%
{{/if}}
{{#unless avoid}}
AVOID
- Awareness spend before distribution is secured
- Unexplained "miscellaneous" buckets over 5%
- Allocations that leave less than 3 months of reserve
{{/unless}}`,
    tips: [
      "Paste table 1 into Google Sheets, link the $ column to the raise amount cell, and share it with your accountant before it goes in the deck.",
      "Follow up: “Re-run at 70% of the raise amount — what gets cut and how does runway change?”",
    ],
  },

  // ── COMPETITIVE ANALYSIS ───────────────────────────────────────────────────
  {
    id: "investor-competitive-analysis",
    category: "investor",
    title: "Create Competitive Analysis",
    description: "An investor-grade competitive analysis — landscape map, comparison table, whitespace and a moat assessment — with facts separated from inference.",
    outputType: "text",
    recommendedTools: ["perplexity-research", "chatgpt", "claude", "gemini-research"],
    estimatedTime: "2 min to fill · 3 min in Perplexity",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims / differentiators", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", required: true, brandKey: "competitors" },
      { id: "pricePoint", label: "Retail price", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC website, Amazon, Natural grocery", brandKey: "channels" },
      { id: "moatHypothesis", label: "Your moat hypothesis (optional)", type: "textarea", placeholder: "Community of trainers; proprietary caffeine blend; first-mover in natural grocery with this format", span: "full" },
      { id: "analysisDepth", label: "Depth", type: "select", options: ["Quick (top 5 competitors)", "Standard (8–10 competitors incl. adjacent)", "Deep (12+ incl. private label and emerging)"], default: "Standard (8–10 competitors incl. adjacent)" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "'No real competitors'; trashing competitors", brandKey: "avoid" },
    ],
    template: `Act as a senior CPG investor and category strategist who writes competitive analyses for food and beverage diligence. Where you have web access, cite sources with links and dates; otherwise label every fact as "known as of training" and mark it [VERIFY].

Create an investor-grade competitive analysis for {{brandName}}, {{category|article}}. Depth: {{analysisDepth}}.

PRODUCT
{{productDescription}}
{{#if pricePoint}}
Retail price: {{pricePoint}}
{{/if}}

TARGET CONSUMER
{{targetConsumer}}

POSITIONING
{{positioning}}

{{#if claims}}
DIFFERENTIATORS WE CLAIM
{{claims|list}}
{{/if}}

NAMED COMPETITORS (start here, then add the ones I am missing)
{{competitors|list}}

{{#if channels}}
OUR CHANNELS
{{channels|join}}
{{/if}}

{{#if moatHypothesis}}
MOAT HYPOTHESIS TO TEST
{{moatHypothesis}}
{{/if}}
{{#unless moatHypothesis}}
MOAT HYPOTHESIS
None supplied — propose the 2 to 3 most credible moat candidates for a brand like this and assess each honestly.
{{/unless}}

PROVIDE
1. Landscape map — define the 2 axes that matter most to {{targetConsumer}} (for example price vs. functional intensity, or natural vs. conventional), place every competitor and {{brandName}} on it, and explain the clusters. Describe the map in text and as a table of coordinates so it can be drawn.
2. Competitor table — one row per competitor: Brand | Parent / funding status | Hero product and format | Price per unit (mark [VERIFY]) | Key claims | Primary channels | Estimated scale (only if sourced; else "undisclosed") | Recent moves | What they do better than us | Where they are vulnerable
3. Tiering — Direct, Adjacent, Private label / value, Emerging — with 2 sentences on the threat each tier poses
4. Whitespace — 3 to 5 specific, defensible gaps in the landscape, each with: the consumer need, evidence it exists, why incumbents have not filled it, and how {{brandName}} occupies it
5. Moat assessment — a table: Moat candidate | Type (brand, community, formulation / IP, distribution, cost, data) | Strength today (1 to 5) | Strength in 3 years if funded | What would erode it | Evidence. Be honest about what is early.
6. Competitive responses — the 3 most likely moves incumbents make if {{brandName}} gains share, and the counter for each
7. Investor summary — 150 words a fund partner could paste into an investment memo, including the one-sentence answer to "why doesn't {{#if competitors}}{{competitors|join}}{{/if}}{{#unless competitors}}the leader{{/unless}} just do this?"
8. Gaps to close — the 5 facts the founder should verify or gather before showing this to investors
9. Source list — URL and date for every cited fact, with anything older than 18 months marked

RULES
Separate facts from inference with clear labels. Do not invent revenue, funding amounts, store counts or velocities — write "undisclosed" or [VERIFY]. Do not make claims about competitors' products that could be defamatory or that you cannot source. Flag any competitor health claim you repeat as [THEIR CLAIM, UNVERIFIED].

{{#if avoid}}
AVOID
{{avoid|list}}
- "We have no real competitors"
- Disparaging competitors
{{/if}}
{{#unless avoid}}
AVOID
- "We have no real competitors"
- Disparaging competitors
- Feature checklists with no point of view
{{/unless}}`,
    tips: [
      "Run in Perplexity Deep Research for cited, current facts; then paste the result into Claude and ask for the investor summary to be sharpened.",
      "Reuse the landscape map (#1) as your pitch-deck competition slide.",
    ],
  },

  // ── INVESTOR MEETING PREP ──────────────────────────────────────────────────
  {
    id: "investor-meeting-prep",
    category: "investor",
    title: "Prepare for Investor Meeting",
    description: "A meeting-prep pack — likely questions by meeting stage, objection handling, the numbers to know cold, a data-room checklist and how to close for the next step.",
    outputType: "text",
    recommendedTools: ["claude", "chatgpt", "gemini"],
    estimatedTime: "3 min to fill · 2 min in Claude",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC website, Amazon, Natural grocery", brandKey: "channels" },
      { id: "stage", label: "Business stage", type: "select", options: STAGE_OPTIONS, default: "Launched — DTC only", brandKey: "stage" },
      { id: "raiseAmount", label: "Raise amount", type: "text", placeholder: "$500,000", required: true },
      { id: "instrument", label: "Instrument", type: "select", options: INSTRUMENT_OPTIONS, default: "SAFE" },
      { id: "traction", label: "Traction to date", type: "textarea", placeholder: "$180k TTM revenue; 2,100 customers; 38% repeat; 40 stores", required: true, span: "full" },
      { id: "investorType", label: "Investor type", type: "select", options: INVESTOR_TYPE_OPTIONS, default: "CPG-focused VC / seed fund" },
      { id: "meetingStage", label: "Meeting stage", type: "select", options: ["First call (20–30 min)", "Full pitch meeting", "Partner meeting", "Diligence deep-dive", "Terms discussion"], default: "First call (20–30 min)" },
      { id: "investorName", label: "Investor name / firm (optional)", type: "text", placeholder: "Maria Chen, Greenhouse Ventures" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Over-rehearsed answers; defensiveness", brandKey: "avoid" },
    ],
    template: `Act as a senior CPG investor and former founder who has sat on both sides of the table and coaches food and beverage founders before investor meetings.

Prepare me for a {{meetingStage|lower}} with an investor of this type: {{investorType}}{{#if investorName}} ({{investorName}}){{/if}} — for {{brandName}}, {{category|article}}, raising {{raiseAmount}}{{#if instrument!=Not decided yet}} (instrument: {{instrument}}){{/if}}.

PRODUCT
{{productDescription}}

{{#if targetConsumer}}
TARGET CONSUMER
{{targetConsumer}}
{{/if}}

POSITIONING
{{positioning}}

{{#if competitors}}
COMPETITORS
{{competitors|join}}
{{/if}}

BUSINESS
- Stage: {{stage}}
{{#if channels}}
- Channels: {{channels|join}}
{{/if}}
- Traction: {{traction}}

{{#if meetingStage=First call (20–30 min)}}
Goal of a first call: clear story, 2 to 3 memorable numbers, and an invitation to a full meeting. Do not try to close.
{{/if}}
{{#if meetingStage=Full pitch meeting}}
Goal: walk the deck in 15 minutes, leave 25 for questions, and secure a partner meeting or diligence start.
{{/if}}
{{#if meetingStage=Partner meeting}}
Goal: the sponsoring partner is selling you internally; arm them. Expect the toughest questions on market size, unit economics and why now.
{{/if}}
{{#if meetingStage=Diligence deep-dive}}
Goal: every number reconciles to the books. Bring the data room; be ready on cohorts, COGS, co-packer terms and cap table.
{{/if}}
{{#if meetingStage=Terms discussion}}
Goal: understand, do not negotiate live. Every term discussed is [REVIEW WITH COUNSEL] before agreeing.
{{/if}}

PROVIDE
1. Meeting objective — the one outcome to leave with and the fallback
2. Agenda — timed for this meeting stage, including where samples come out
3. 15 questions this investor type asks at this stage, each with: what they are really testing, a model answer (3 to 5 sentences, first person, only supplied facts, [NUMBER NEEDED] where a figure is missing)
4. Objection handling — the 8 most likely pushbacks (market too niche, CAC too high, retail is a capital trap, big brands will copy you, too early, single-founder risk, valuation, why not bootstrap) each with a 3-sentence response and the proof to show
5. Numbers to know cold — a table: Metric | Your number (or [INSERT]) | Why it matters | Typical range investors expect for {{category|lower}} at this stage (label as typical, [VERIFY])
6. Data-room checklist — what to have ready for this stage: financials, cohort data, COGS and co-packer agreements, cap table, distribution agreements, IP and trademarks, regulatory and label compliance, insurance, key contracts; mark each as "have / need / n/a" for the founder to fill
7. Opening 90 seconds — word for word
8. Questions to ask the investor — 6 questions that show judgment (check size, lead or follow, CPG portfolio, how they help with retail, decision process and timeline, reserves)
9. Closing — how to ask for the next step and the exact follow-up to send within 24 hours
10. 5 things to never say in this meeting

RULES
Use only supplied facts. Do not propose valuation, cap, discount or terms — write [DISCUSS WITH COUNSEL]. This is not financial, legal or investment advice; validate projections with the founder's accountant and all terms with a securities attorney.

{{#if avoid}}
AVOID
{{avoid|list}}
- Over-rehearsed or defensive answers
- Overstating traction or commitments
{{/if}}
{{#unless avoid}}
AVOID
- Over-rehearsed or defensive answers
- Overstating traction or commitments
- Criticizing other investors or competitors by name
{{/unless}}`,
    tips: [
      "Rehearse: “Be this investor. Run the meeting in real time — ask one question, wait for my answer, then critique it.”",
      "Pair with Create Investor FAQ for the full 25-question bank.",
    ],
  },

  // ── MONTHLY INVESTOR UPDATE ────────────────────────────────────────────────
  {
    id: "investor-update-email",
    category: "investor",
    title: "Write Monthly Investor Update",
    description: "A monthly investor update email — headline metrics, wins, challenges, asks and runway — in a repeatable template investors actually read and reply to.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "3 min to fill · 30 sec in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC website, Amazon, Natural grocery", brandKey: "channels" },
      { id: "stage", label: "Business stage", type: "select", options: STAGE_OPTIONS, default: "Launched — DTC only", brandKey: "stage" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice" },
      { id: "updatePeriod", label: "Period", type: "text", placeholder: "March 2026", required: true },
      { id: "metrics", label: "This month's metrics", type: "textarea", placeholder: "Revenue $21k (+18% MoM); gross margin 52%; 2,340 customers; repeat 39%; 44 stores; cash $310k; burn $24k", required: true, span: "full" },
      { id: "wins", label: "Wins", type: "textarea", placeholder: "Approved in 60 Sprouts SoCal stores; featured in Well+Good; hired first sales lead", required: true, span: "full" },
      { id: "challenges", label: "Challenges / lowlights", type: "textarea", placeholder: "Co-packer delay pushed run 3 weeks; Meta CAC up 22%", span: "full" },
      { id: "asks", label: "Asks for investors", type: "textarea", placeholder: "Intro to a KeHE category manager; recommend a fractional CFO; feedback on pricing", span: "full" },
      { id: "audienceType", label: "Audience", type: "select", options: ["Current investors only", "Investors + advisors", "Investors + prospective investors (lighter on financials)"], default: "Current investors only" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Spin; burying bad news; walls of text", brandKey: "avoid" },
    ],
    template: `Act as a senior CPG investor who reads 40 founder updates a month and coaches food and beverage founders on the ones that earn help and follow-on checks.

Write the {{updatePeriod}} investor update for {{brandName}}, {{category|article}}, sent to: {{audienceType|lower}}.

{{#if productDescription}}
PRODUCT
{{productDescription}}
{{/if}}
{{#if positioning}}
Positioning: {{positioning}}
{{/if}}
Stage: {{stage}}
{{#if channels}}
Channels: {{channels|join}}
{{/if}}

THIS MONTH'S METRICS (use exactly — do not compute or invent anything not here)
{{metrics}}

WINS
{{wins}}

CHALLENGES
{{challenges|default:None supplied — include the section anyway with a [FOUNDER: add at least one honest lowlight] placeholder; updates with no lowlights lose credibility.}}

ASKS
{{asks|default:None supplied — include the section with 2 suggested asks typical for a brand at this stage, marked [FOUNDER TO CONFIRM].}}

VOICE
{{brandVoice|default:Plain, direct, confident, honest. Short paragraphs. Numbers first.}}

{{#if audienceType=Investors + prospective investors (lighter on financials)}}
Prospective investors are on this list: keep cash and burn out of the body (move to a "current investors only" appendix note) and avoid anything that could read as a general solicitation — mark the whole email [REVIEW WITH COUNSEL BEFORE SENDING TO NON-INVESTORS].
{{/if}}

PROVIDE
1. Subject line — 3 options in the format "{{brandName}} — {{updatePeriod}} update: [one headline number or win]"
2. The email (350 to 500 words) with these labeled sections in order:
   TL;DR — 3 bullets: the headline metric, the biggest win, the biggest ask
   METRICS — a compact table: Metric | This month | Last month [INSERT] | Change | Note. Only supplied numbers; never compute a change without both values.
   WINS — 3 to 5 bullets, specific, with the number or name that makes each real
   CHALLENGES — 2 to 3 bullets, each with what we are doing about it
   ASKS — numbered, each one sentence, each with a specific person or type of person who could help
   CASH AND RUNWAY — one line ([INSERT] if not in metrics), marked [VALIDATE WITH ACCOUNTANT]
   NEXT MONTH — 3 priorities
   THANK YOU — one line naming any investor who helped this month [INSERT NAMES]
3. A reusable monthly template version with every section and placeholder, so next month takes 15 minutes
4. A 3-sentence version for a text or WhatsApp to your lead investor
5. Cadence and hygiene notes — send date, consistency of metrics month to month, what to never put in writing without counsel (forward-looking promises, anything about terms of a future round)

RULES
Use only supplied figures. Never invent, round or extrapolate numbers. Bad news goes in, clearly and early. Not financial or legal advice — flag financials for the accountant and any fundraising language for counsel.

{{#if avoid}}
AVOID
{{avoid|list}}
- Spin or burying bad news
- Walls of text
{{/if}}
{{#unless avoid}}
AVOID
- Spin or burying bad news
- Walls of text
- Metrics that change definition month to month
{{/unless}}`,
    tips: [
      "Save the template (#3) as a doc and fill it on the same day each month — consistency is what investors reward.",
      "Follow up: “Rewrite the challenges section so it sounds like a plan, not an apology.”",
    ],
  },
];
